import React, {useEffect, useMemo, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import ImageBasic from "../Image/ImageBasic";
import IMG from "../../../constants/image";
import {useSearchParams} from "react-router-dom";
import {isEmpty} from "lodash";
import {useScrollTabIntoView} from "../../../hooks/useScrollTabIntoView";

const CustomTabs = ({renderContent, handleChangeTab, tabs, tabActive}) => {
    const [searchParams] = useSearchParams();
    const searchStatus = searchParams.get('status');
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    // Use the custom hook for scrolling tabs into view
    const tabsContainerRef = useScrollTabIntoView(activeTab?.toString());

    useEffect(() => {
        if (tabActive && activeTab !== tabActive?.id) {
            setActiveTab(tabActive?.id);
        }
    }, [tabActive])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "ArrowRight") {
                setActiveTab((prev) => (prev + 1) % tabs.length);
            } else if (event.key === "ArrowLeft") {
                setActiveTab((prev) => (prev - 1 + tabs.length) % tabs.length);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [tabs]);


    const activeItemQuery = useMemo(() => {
        if (isEmpty(tabs) || !searchStatus) return null;
        return tabs.filter((item) => item?.status === searchStatus);
    }, [tabs, searchStatus])

    useEffect(() => {
        if (!isEmpty(activeItemQuery)) {
            setIsLoading(true);
            setActiveTab(activeItemQuery?.[0]?.id);
            setTimeout(() => setIsLoading(false), 500);
            handleChangeTab(tabs[activeItemQuery?.[0]?.id]);
        }
    }, [activeItemQuery]);

    const handleTabClick = (index) => {
        setIsLoading(true);
        setActiveTab(index);
        setTimeout(() => setIsLoading(false), 500);
        handleChangeTab && handleChangeTab(tabs[index]);
    };

    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && activeTab < tabs.length - 1) {
            setActiveTab((prev) => prev + 1);
        } else if (isRightSwipe && activeTab > 0) {
            setActiveTab((prev) => prev - 1);
        }
    };

    return (
        <div className="w-full bg-white px-4">
            <div
                className="flex overflow-x-auto no-scrollbar space-x-[10px] md:space-x-[16px] mt-4 md:mt-0"
                ref={tabsContainerRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {tabs.map((tab, index) => (
                    <button
                        key={tab.id}
                        id={tab.id}
                        onClick={() => handleTabClick(index)}
                        className={`relative flex-shrink-0 py-2 text-sm ${
                            activeTab === index
                                ? "font-bold border-b-4 border-[#9E9E9E]"
                                : ""
                        }`}
                        role="tab"
                        aria-selected={activeTab === index}
                        aria-controls={`tabpanel-${index}`}
                    >
                        {`${tab.title} (${tab.count})`}
                        {
                            tab?.redSight && (
                                <div
                                    className='absolute top-2 right-[-10px] bg-[#E4626F] w-[9px] h-[9px] rounded-full'></div>
                            )
                        }
                    </button>
                ))}
            </div>

            <div className="bg-white border-t border-gray-200 rounded-b-lg py-4 min-h-[200px] relative">
                <AnimatePresence mode="wait">
                    {isLoading ? (
                        <motion.div
                            key="loader"
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            exit={{opacity: 0}}
                            className="absolute inset-0 flex flex-col items-center justify-center"
                        >
                            <ImageBasic className='w-20 h-20' src={IMG.gif.loading}/>
                            <div className='text-sm text-black'>Vui lòng đợi trong giây lát</div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key={activeTab}
                            initial={{opacity: 0, y: 10}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: -10}}
                            transition={{duration: 0.2}}
                            id={`tabpanel-${activeTab}`}
                            role="tabpanel"
                            aria-labelledby={`tab-${activeTab}`}
                        >
                            {renderContent()}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default CustomTabs;